<template>
  <div
    class="easy-time-picker"
    :class="{ 'easy-time-picker--focus': isTimeInputFocused }"
  >
    <input
      ref="hoursInput"
      v-click-outside="{
        handler: () => handleClickOutsideTimeParts(TimeParts.Hours),
        include: () => [props.menuEl],
      }"
      type="text"
      placeholder="--"
      class="easy-time-picker__hour"
      :class="{ 'easy-time-picker__hour--half': !useMeridiem }"
      :value="hoursValue"
      @focus="inputFocuses[TimeParts.Hours] = true"
      @input="handleTimePartsInput($event, TimeParts.Hours)"
      @keydown.stop="handleKeyDown($event, TimeParts.Hours)"
      @change="emit('change')"
    />
    <span class="easy-time-picker__colon">:</span>
    <input
      ref="minutesInput"
      v-click-outside="{
        handler: () => handleClickOutsideTimeParts(TimeParts.Minutes),
        include: () => [props.menuEl],
      }"
      type="text"
      placeholder="--"
      class="easy-time-picker__minute"
      :value="minutesValue"
      @focus="inputFocuses[TimeParts.Minutes] = true"
      @input="handleTimePartsInput($event, TimeParts.Minutes)"
      @keydown.stop="handleKeyDown($event, TimeParts.Minutes)"
      @change="emit('change')"
    />
    <input
      v-if="useMeridiem"
      ref="meridiemInput"
      v-click-outside="{
        handler: () => (inputFocuses[TimeParts.Meridiem] = false),
        include: () => [props.menuEl],
      }"
      type="text"
      placeholder="--"
      class="easy-time-picker__meridiem"
      :value="meridiemValue"
      @focus="inputFocuses[TimeParts.Meridiem] = true"
      @input="handleMeridiemInput"
      @keydown.stop="handleKeyDown($event, TimeParts.Meridiem)"
      @change="emit('change')"
    />
  </div>
</template>

<script setup lang="ts">
import easyTimePickerUtils from "../../utils/easyTimePicker";
import { EasyTimePickerInputEmits, EasyTimePickerInputProps, TimeParts } from "../../types/easyTimePicker";
import { MAX_HOURS_TIME_INPUT_24H } from "@/src/shared/constants/timeEntry";
import { computed, reactive, ref, watch } from "vue";
import { useFocusEvents } from "../../composables/easyTimePicker/focusEvents";
import { useGetSetHelpers } from "../../composables/easyTimePicker/getSetHelpers";
import { useKeyboardDownEvents } from "../../composables/easyTimePicker/keydownEvents";
import { BLUR_DELAY_MS } from "../../constants/easyTimePicker";

const props = withDefaults(defineProps<EasyTimePickerInputProps>(), {
  disabled: false,
  useMeridiem: false,
  maxHours: MAX_HOURS_TIME_INPUT_24H,
  menuEl: undefined,
});

const emit = defineEmits<EasyTimePickerInputEmits>();

const hoursInput = ref<HTMLInputElement>();
const minutesInput = ref<HTMLInputElement>();
const meridiemInput = ref<HTMLInputElement>();
const inputFocuses = reactive({
  [TimeParts.Hours]: false,
  [TimeParts.Minutes]: false,
  [TimeParts.Meridiem]: false,
});

const composablesData = { inputFocuses, hoursInput, minutesInput, meridiemInput };
const { handleKeyDown } = useKeyboardDownEvents(props, emit, composablesData);
const { focusAndSetCursorOnNextInput, handleClickOutsideTimeParts } = useFocusEvents(props, emit, composablesData);
const { setInputValue } = useGetSetHelpers(props, emit, composablesData);

const isTimeInputFocused = computed(() => Object.values(inputFocuses).some(focus => focus));
watch(
  () => isTimeInputFocused.value,
  isFocused => triggerTimeEmits(isFocused)
);

const shouldEmitUpdateModel = (isFocused: boolean) => !isFocused && (isTimeValid.value || hasEmptyValue.value);
const triggerTimeEmits = (isCurrentlyFocused: boolean) => {
  if (shouldEmitUpdateModel(isCurrentlyFocused)) {
    setTimeout(() => {
      if (shouldEmitUpdateModel(isTimeInputFocused.value)) emit("update:timeValue");
    }, BLUR_DELAY_MS);
  }
  if (!isCurrentlyFocused) {
    setTimeout(() => {
      if (!isTimeInputFocused.value) emit("blur");
    }, BLUR_DELAY_MS);
  }
  if (isTimeInputFocused.value) emit("focus");
};

const handleTimePartsInput = (e: Event, part: TimeParts) => {
  const inputEvent = e as InputEvent;
  const target = inputEvent.target as HTMLInputElement;
  const max = part === TimeParts.Minutes ? 59 : props.maxHours;
  const validInput = easyTimePickerUtils.validateTimePart(target.value, inputEvent.data, max);
  target.value = validInput;
  setInputValue(part, validInput);

  if (validInput.length > 1 && inputEvent.inputType === "insertText") {
    focusAndSetCursorOnNextInput(part);
  }
};

const handleMeridiemInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const validInput = easyTimePickerUtils.validateMeridiem(target.value);
  target.value = validInput;
  setInputValue(TimeParts.Meridiem, validInput);
};

const isTimeValid = computed(() => {
  return props.hoursValue !== "" && props.minutesValue !== "" && !!(!props.useMeridiem || props.meridiemValue);
});

const hasEmptyValue = computed(() => {
  const isEmptyForHHmmFormat = props.hoursValue === "" && props.minutesValue === "";
  if (!props.useMeridiem) return isEmptyForHHmmFormat;
  return isEmptyForHHmmFormat && props.meridiemValue === "";
});
</script>
